import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';

import { ShotResponse } from '../../models/shots/shotResponse';
import { ShotFromResponse } from '../../models/shots/shotFromResponse';
import { ScorecardService } from '../../services/scorecard/scorecard.service';
import { ShotTypeResponse } from '../../models/shots/shotTypeResponse';
import { ConfirmationDialogComponent } from '../shared/confirmation-dialog/confirmation-dialog.component';
import { ScoreResponse } from '../../models';
import { ShotStatsService } from '../../services/scorecard/shot-stats/shot-stats.service';
import { ShotFrom, ShotType } from '../../models/shots/shotEnums';

export type ShotListModalParams = {
  playerName: string;
  par: number;
  score: ScoreResponse;
};

@Component({
  selector: 'app-shot-list-modal',
  standalone: true,
  imports: [CommonModule, DragDropModule, ConfirmationDialogComponent],
  templateUrl: './shot-list-modal.component.html',
  styleUrl: './shot-list-modal.component.scss',
})
export class ShotListModalComponent implements OnInit {
  @ViewChild('confirmDeleteDialog') confirmDeleteDialog!: ConfirmationDialogComponent;
  @ViewChild('confirmSaveDialog') confirmSaveDialog!: ConfirmationDialogComponent;
  @ViewChild('confirmInTheHoleDialog') confirmInTheHoleDialog!: ConfirmationDialogComponent;
  @ViewChildren('shotFromButton') shotFromButtons!: QueryList<ElementRef<HTMLButtonElement>>;

  isVisible = false;
  isDirty = false;
  private shouldFocusNewShot = false;

  @Output() shotsUpdated = new EventEmitter<ShotListModalParams>();

  private shotFroms: ShotFromResponse[] = [];
  private shotTypes: ShotTypeResponse[] = [];
  shotFromsAndTypes: (ShotFromResponse | ShotTypeResponse)[] = [];

  // Stats
  fwys = false;
  gir = false;
  ss = false;
  putts = 0;
  score = 0;

  public get isPar3(): boolean {
    return this.params.par === 3;
  }

  constructor(
    private readonly scorecardsService: ScorecardService,
    private readonly shotStatsService: ShotStatsService
  ) { }

  ngOnInit(): void {
    this.scorecardsService
      .getShotFroms()
      .subscribe((shotFroms) => {
        this.shotFroms = shotFroms.sort((a, b) =>
          (a.sortPriority ?? Number.MAX_VALUE) - (b.sortPriority ?? Number.MAX_VALUE)
        );
        this.updateShotFromsAndTypes();
      });

    this.scorecardsService
      .getShotTypes()
      .subscribe((shotTypes) => {
        this.shotTypes = shotTypes.sort((a, b) =>
          (a.sortPriority ?? Number.MAX_VALUE) - (b.sortPriority ?? Number.MAX_VALUE)
        );
        this.updateShotFromsAndTypes();
      });
  }

  private updateShotFromsAndTypes(): void {
    const widestItemWidth = Math.max(
      ...this.shotFroms.map(item => item.name!.length * 2)
    );

    const seperatorItem: ShotFromResponse = {
      id: -1,
      shotFromId: -1,
      name: '-'.repeat(widestItemWidth),
      sortPriority: Number.MAX_VALUE,
    };

    const filteredShotTypes = this.shotTypes.filter(
      (shotType) => shotType.shotTypeId !== undefined &&
        shotType.shotTypeId > 0 &&
        shotType.name !== 'Normal'
    );

    this.shotFromsAndTypes = [...this.shotFroms, seperatorItem, ...filteredShotTypes];
  }

  private _params: ShotListModalParams = {
    playerName: '',
    par: 0,
    score: { shots: [] as ShotResponse[] } as ScoreResponse,
  };

  get params(): ShotListModalParams {
    return this._params;
  }

  set params(value: ShotListModalParams) {
    this._params = this.cloneParams(value);
    this.calculateStats();
  }

  openModal(params: ShotListModalParams): void {
    this.params = params;

    this.isDirty = false;
    this.isVisible = true;
  }

  save() {
    this.shotsUpdated.emit(this.cloneParams(this._params));

    this.isDirty = false;
    this.closeModal();
  }

  closeModal(): void {
    this.isVisible = false;
  }

  addShot(): void {
    if (this.params) {
      const shotsLength = this.params.score?.shots?.length ?? 0;
      const number = shotsLength > 0
        ? shotsLength + 1
        : 1;
      const NEW_SHOT_DEFAULT_NAME = '--- Select a Lie ---';
      const newShot: ShotResponse = {
        shotId: 'new-unset',
        number,
        shotFrom: undefined,
        shotType: 1,
        shotFromName: NEW_SHOT_DEFAULT_NAME,
        shotTypeName: NEW_SHOT_DEFAULT_NAME,
        submittedAt: this.getSubmittedAt(),
      };
      this.params.score?.shots?.push(newShot);

      this.isDirty = true;
      this.calculateStats();
      this.shouldFocusNewShot = true;
      setTimeout(() => this.focusLatestShotDropdown());
    }
  }

  private getSubmittedAt(): Date {
    return new Date();
  }

  get shots(): ShotResponse[] {
    return this.params?.score?.shots!;
  }

  private cloneParams(params: ShotListModalParams): ShotListModalParams {
    const score = params.score ?? ({} as ScoreResponse);

    const clonedShots = (score.shots ?? []).map((shot) => this.cloneShot(shot));

    const clonedOriginalShots = score.originalShots
      ? score.originalShots.map((shot) => ({
          ...shot,
          submittedAt: shot.submittedAt ? new Date(shot.submittedAt) : undefined,
          createdOnUtc: shot.createdOnUtc ? new Date(shot.createdOnUtc) : undefined,
          modifiedOnUtc: shot.modifiedOnUtc ? new Date(shot.modifiedOnUtc) : undefined,
        }))
      : score.originalShots;

    const clonedScore: ScoreResponse = {
      ...score,
      shots: clonedShots,
      originalShots: clonedOriginalShots,
    };

    return {
      ...params,
      score: clonedScore,
    };
  }

  private cloneShot(shot: ShotResponse): ShotResponse {
    return {
      ...shot,
      submittedAt: shot.submittedAt ? new Date(shot.submittedAt) : undefined,
      createdOnUtc: shot.createdOnUtc ? new Date(shot.createdOnUtc) : undefined,
      modifiedOnUtc: shot.modifiedOnUtc ? new Date(shot.modifiedOnUtc) : undefined,
    };
  }

  selectShotFromOrType(shot: ShotResponse, option: ShotFromResponse | ShotTypeResponse): void {
    if (option.id === -1) {
      // This is a separator item, do nothing
      return;
    }

    if ('shotTypeId' in option) {

      // This is a ShotTypeResponse
      shot.shotType = option.shotTypeId;
      shot.shotTypeName = option.name;
      shot.shotFrom = ShotFrom.Other;
      shot.shotFromName = option.name;

    } else if ('shotFromId' in option) {

      // This is a ShotFromResponse
      shot.shotFrom = option.shotFromId;
      shot.shotFromName = option.name;
      shot.shotType = ShotType.Normal;
      shot.shotTypeName = 'Normal';
    }

    shot.shotId = 'selected';

    this.isDirty = true;
    this.calculateStats();
  }

  drop(event: CdkDragDrop<any[]>) {
    if (this.params?.score?.shots && event.previousIndex !== event.currentIndex) {
      const items = this.params.score.shots;
      // Move the item in the array
      const item = items[event.previousIndex];
      items.splice(event.previousIndex, 1);
      items.splice(event.currentIndex, 0, item);

      // Update shot numbers after reordering
      this.params.score.shots.forEach((shot, index) => {
        shot.number = index + 1;
      });

      this.isDirty = true;
      this.calculateStats();
    }
  }

  private calculateStats(): void {
    this.shotStatsService.calculateAllStats(
      this.params.score,
      this.params.par
    );

    // Update local stats variables used in the UI
    if (this.params.score) {
      this.putts = this.params.score.putts ?? 0;
      this.score = this.params.score.score ?? 0;
      this.fwys = this.params.score.fairway ?? false;
      this.gir = this.params.score.gir ?? false;
      this.ss = this.params.score.sandSaveMade ?? false;
    }

  }

  private focusLatestShotDropdown(): void {
    if (!this.shouldFocusNewShot) {
      return;
    }

    const lastButton = this.shotFromButtons?.last?.nativeElement;

    if (lastButton) {
      lastButton.focus();
    }

    this.shouldFocusNewShot = false;
  }

  deleteShot(shot: ShotResponse): void {
    if (this.isAddingShot()) {
      this._deleteShot(shot);
      return;
    }

    this.confirmDeleteDialog.open({
      title: `Delete Shot ${shot.number} from the ${shot.shotFromName}?`,
      message: `Are you sure you want to delete Shot ${shot.number}?`,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      dangerMode: true,
      data: { ...shot },
    });
  }

  private _deleteShot(shot: ShotResponse): void {
    if (this.params) {
      this.params.score.shots =
        this.params.score.shots?.filter((s) => s.number !== shot.number) || [];
    }
    // Reset shot numbers after deletion
    this.params?.score.shots?.forEach((s, index) => {
      s.number = index + 1;
    });

    this.isDirty = true;
    this.calculateStats();
  }

  onSaveChangesConfirmed(): void {
    this.save();
  }

  onSaveChangesCancelled(): void {
    this.isDirty = false;
    this.isVisible = false;
  }

  onDeleteShotConfirmed(shot: any): void {
    this._deleteShot(shot);
  }

  onDeleteShotCancelled(): void {
  }

  isHoleComplete(): boolean {
    return this.params.score.statusCode !== 'I';
  }

  isAddingShot(): boolean {
    return this.params.score.shots?.some(shot => shot.shotId === 'new-unset')!;
  }

  hasNoShots(): boolean {
    return !this.params.score.shots || this.params.score.shots.length === 0;
  }

  tapIn(): void {
    const shotFromName = this.shotFroms.find(sf => sf.shotFromId === ShotFrom.Green)?.name || 'Green';
    const shotTypeName = this.shotTypes.find(st => st.shotTypeId === ShotType.Normal)?.name || 'Normal';

    const newShot: ShotResponse = {
      shotId: 'new-set',
      number: this.params.score.shots!.length + 1,
      shotFrom: ShotFrom.Green,
      shotFromName,
      shotType: ShotType.Normal,
      shotTypeName,
      submittedAt: this.getSubmittedAt()
    };
    this.params.score.shots!.push(newShot);
    this.markInTheHole();
  }

  private getShotOutcomeText(): string {
    const shotsTaken = this.params.score.shots?.length ?? 0;

    if (shotsTaken === 1) {
      return 'Ace (Hole-in-One)';
    }

    const par = this.params.par;

    if (shotsTaken === 0 || par === 0) {
      return 'Unknown';
    }

    const difference = shotsTaken - par;

    let outcome: string;
    switch (difference) {
      case -3:
        outcome = 'Double Eagle';
        break;
      case -2:
        outcome = 'Eagle';
        break;
      case -1:
        outcome = 'Birdie';
        break;
      case 0:
        outcome = 'Par';
        break;
      case 1:
        outcome = 'Bogey';
        break;
      case 2:
        outcome = 'Double Bogey';
        break;
      case 3:
        outcome = 'Triple Bogey';
        break;
      case 4:
        outcome = 'Quadruple Bogey';
        break;
      default:
        outcome = `${difference}-over-par`;
        break;
    }

    return `${outcome} ${shotsTaken}`;
  }

  markInTheHole(): void {

    if (!this.isAddingShot()) {
      this.confirmInTheHoleDialog.open({
        title: 'In The Hole?',
        message: `Did the last shot go in the hole for a ${this.getShotOutcomeText()}?`,
        confirmButtonText: 'Yes, it did',
        cancelButtonText: 'Cancel',
        dangerMode: false,
      });
    }

  }

  private markShotsInTheHole(inTheHole: boolean = true): void {
    this.params.score.shots?.forEach(shot => {
      shot.inTheHole = inTheHole;
    });
  }

  get displayPutts(): string | number {
    return this.putts ? this.putts : '';
  }

  get displayScore(): string | number {
    return this.score ? this.score : '';
  }

  markIncomplete(): void {
    this.params.score.statusCode = 'I';

    this.markShotsInTheHole(false);

    this.isDirty = true;
    this.calculateStats();
  }

  canBack(): boolean {
    return !this.isAddingShot();
  }

  handleBackClick(): void {
    if (this.isDirty) {
      this.confirmSaveDialog.open({
        title: 'Save Changes?',
        message: 'You have unsaved changes. Save them or Cancel to exit without saving.',
        confirmButtonText: 'Save',
        cancelButtonText: 'Cancel',
        dangerMode: false,
      });
    } else {
      this.closeModal();
    }
  }

  onInTheHoleConfirmed(): void {
    this.params.score.statusCode = 'C';

    this.markShotsInTheHole();

    this.isDirty = true;
    this.calculateStats();
  }

  onInTheHoleCancelled(): void {
    this.markShotsInTheHole(false);
  }

}
